import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { Search, Person, AccountBalance } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import LoadingAnimation from '../components/LoadingAnimation';
import { openFilePreviewOrDownload } from '../utils/filePreview';
import PasswordVerificationModal from '../components/PasswordVerificationModal';
import { authService } from '../services/authService';
import { useHRAccess } from '../hooks/useHRAccess';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';
import { labelsForKeys } from '../utils/formValidation';

const EXPENSE_FIELD_LABELS: Record<string, string> = {
  expense_date: 'Date',
  category: 'Category',
  amount: 'Amount',
  description: 'Description',
};

const ExpensesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [passwordVerificationOpen, setPasswordVerificationOpen] = useState(false);
  const { isVerified: passwordVerified, grantAccess } = useHRAccess();
  const [expenseClaims, setExpenseClaims] = useState<any[]>([]);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expensePage, setExpensePage] = useState(1);
  const [expenseTotalPages, setExpenseTotalPages] = useState(1);
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [expenseStatusFilter, setExpenseStatusFilter] = useState('');
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('');
  const [expenseEmployeeFilter, setExpenseEmployeeFilter] = useState('');
  const [expenseDateFrom, setExpenseDateFrom] = useState('');
  const [expenseDateTo, setExpenseDateTo] = useState('');
  const [submitExpenseDialogOpen, setSubmitExpenseDialogOpen] = useState(false);
  const [reviewExpenseDialogOpen, setReviewExpenseDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<any>(null);
  const [expenseFormData, setExpenseFormData] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    category: '',
    amount: '',
    currency: 'EUR',
    description: ''
  });
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'pay'>('approve');
  const [reviewData, setReviewData] = useState({
    approved_amount: '',
    rejection_reason: '',
    notes: ''
  });
  const [updateExpenseDialogOpen, setUpdateExpenseDialogOpen] = useState(false);
  const [selectedExpenseForUpdate, setSelectedExpenseForUpdate] = useState<any>(null);
  const [updateExpenseFormData, setUpdateExpenseFormData] = useState({
    expense_date: '',
    category: '',
    amount: '',
    currency: 'EUR',
    description: '',
    status: ''
  });
  const [expenseCategories, setExpenseCategories] = useState<string[]>([]);
  const fieldErrors = useFormFieldErrors();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  const fetchExpenseClaims = async () => {
    try {
      setExpenseLoading(true);
      const params = new URLSearchParams({
        page: expensePage.toString(),
        per_page: '10',
        search: expenseSearchTerm,
        status: expenseStatusFilter,
        category: expenseCategoryFilter,
        employee: expenseEmployeeFilter,
        date_from: expenseDateFrom,
        date_to: expenseDateTo
      });

      const response = await fetch(`/api/hr/expenses?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExpenseClaims(data.expenses);
        setExpenseTotalPages(data.pagination.pages);
      } else {
        throw new Error('Failed to fetch expense claims');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load expense claims', severity: 'error' });
    } finally {
      setExpenseLoading(false);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await fetch('/api/hr/expenses/categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExpenseCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to load expense categories:', error);
    }
  };

  const handleSubmitExpense = async () => {
    const missing: string[] = [];
    if (!expenseFormData.expense_date) missing.push('expense_date');
    if (!expenseFormData.category) missing.push('category');
    if (!expenseFormData.amount) missing.push('amount');
    if (!expenseFormData.description) missing.push('description');
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForKeys(missing, EXPENSE_FIELD_LABELS));
      return;
    }
    const submitAmount = parseFloat(expenseFormData.amount);
    if (isNaN(submitAmount) || submitAmount <= 0) {
      fieldErrors.show('Please enter a valid amount greater than 0', ['amount']);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('expense_date', expenseFormData.expense_date);
      formData.append('category', expenseFormData.category);
      formData.append('amount', expenseFormData.amount);
      formData.append('currency', expenseFormData.currency);
      formData.append('description', expenseFormData.description);

      const response = await fetch('/api/hr/expenses/submit', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setSubmitExpenseDialogOpen(false);
        setExpenseFormData({
          expense_date: new Date().toISOString().split('T')[0],
          category: '',
          amount: '',
          currency: 'EUR',
          description: ''
        });
        fetchExpenseClaims();
      } else {
        setSnackbar({ open: true, message: result.error || 'Submission failed', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Submission failed', severity: 'error' });
    }
  };

  const handleReviewExpense = async () => {
    if (!selectedExpense) return;

    try {
      const payload: any = {
        action: reviewAction,
        notes: reviewData.notes
      };

      if (reviewAction === 'approve') {
        payload.approved_amount = reviewData.approved_amount || selectedExpense.amount;
      } else if (reviewAction === 'reject') {
        payload.rejection_reason = reviewData.rejection_reason;
      }

      const response = await fetch(`/api/hr/expenses/${selectedExpense.id}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setReviewExpenseDialogOpen(false);
        setSelectedExpense(null);
        setReviewData({ approved_amount: '', rejection_reason: '', notes: '' });
        fetchExpenseClaims();
      } else {
        setSnackbar({ open: true, message: result.error || 'Review failed', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Review failed', severity: 'error' });
    }
  };

  const handleOpenUpdateExpenseDialog = (expense: any) => {
    setSelectedExpenseForUpdate(expense);
    setUpdateExpenseFormData({
      expense_date: expense.expense_date,
      category: expense.category,
      amount: expense.amount.toString(),
      currency: expense.currency,
      description: expense.description,
      status: expense.status
    });
    setUpdateExpenseDialogOpen(true);
  };

  const handleUpdateExpense = async () => {
    if (!selectedExpenseForUpdate) return;

    // Validation
    const missing: string[] = [];
    if (!updateExpenseFormData.expense_date) missing.push('expense_date');
    if (!updateExpenseFormData.category) missing.push('category');
    if (!updateExpenseFormData.amount) missing.push('amount');
    if (!updateExpenseFormData.description) missing.push('description');
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForKeys(missing, EXPENSE_FIELD_LABELS));
      return;
    }

    const amount = parseFloat(updateExpenseFormData.amount);
    if (isNaN(amount) || amount <= 0) {
      fieldErrors.show('Please enter a valid amount greater than 0', ['amount']);
      return;
    }

    try {
      const payload = {
        expense_date: updateExpenseFormData.expense_date,
        category: updateExpenseFormData.category,
        amount: amount,
        currency: updateExpenseFormData.currency,
        description: updateExpenseFormData.description,
        status: updateExpenseFormData.status
      };

      const response = await fetch(`/api/hr/expenses/${selectedExpenseForUpdate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({ open: true, message: 'Expense updated successfully', severity: 'success' });
        setUpdateExpenseDialogOpen(false);
        setSelectedExpenseForUpdate(null);
        setUpdateExpenseFormData({
          expense_date: '',
          category: '',
          amount: '',
          currency: 'EUR',
          description: '',
          status: ''
        });
        fetchExpenseClaims();
      } else {
        setSnackbar({ open: true, message: result.error || 'Update failed', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Update failed', severity: 'error' });
    }
  };

  const handleExportExpensesPDF = async () => {
    try {
      const params = new URLSearchParams({
        search: expenseSearchTerm,
        status: expenseStatusFilter,
        category: expenseCategoryFilter,
        employee: expenseEmployeeFilter,
        date_from: expenseDateFrom,
        date_to: expenseDateTo
      });

      const response = await fetch(`/api/hr/expenses/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        openFilePreviewOrDownload(
          blob,
          `expense_report_${new Date().toISOString().split('T')[0]}.pdf`,
          'application/pdf'
        );

        setSnackbar({ open: true, message: 'Expense report exported successfully', severity: 'success' });
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: errorData.error || 'Export failed', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  useEffect(() => {
    // Check if password verification is needed
    if (!passwordVerified) {
      setPasswordVerificationOpen(true);
    }
  }, [passwordVerified]);

  useEffect(() => {
    if (passwordVerified) {
      fetchExpenseClaims();
    }
  }, [expensePage, expenseSearchTerm, expenseStatusFilter, expenseCategoryFilter, expenseEmployeeFilter, expenseDateFrom, expenseDateTo, passwordVerified]);

  useEffect(() => {
    if (passwordVerified) {
      fetchExpenseCategories();
    }
  }, [passwordVerified]);

  const handlePasswordVerification = async (password: string) => {
    try {
      const response = await authService.verifyPassword(password);
      return response.data.verified;
    } catch (error) {
      return false;
    }
  };

  const handlePasswordVerified = () => {
    grantAccess();
    setPasswordVerificationOpen(false);
  };

  if (!passwordVerified) {
    return (
      <PasswordVerificationModal
        open={passwordVerificationOpen}
        onClose={() => setPasswordVerificationOpen(false)}
        onVerify={handlePasswordVerification}
        onVerified={handlePasswordVerified}
        title="Access HR Management"
        description="Please verify your password to access HR management features."
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Expenses Register
        </Typography>
        <Button
          variant="contained"
          startIcon={<AccountBalance />}
          onClick={() => setSubmitExpenseDialogOpen(true)}
        >
          Submit Expense
        </Button>
      </Box>

      {/* Expenses Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search expenses"
          variant="outlined"
          value={expenseSearchTerm}
          onChange={(e) => setExpenseSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={expenseStatusFilter}
            onChange={(e) => setExpenseStatusFilter(e.target.value)}
            label="Status"
            MenuProps={getZoomCompensatedSelectMenuProps()}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={expenseCategoryFilter}
            onChange={(e) => setExpenseCategoryFilter(e.target.value)}
            label="Category"
            MenuProps={getZoomCompensatedSelectMenuProps()}
          >
            <MenuItem value="">All</MenuItem>
            {expenseCategories.map((category) => (
              <MenuItem key={category} value={category}>{category}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Employee"
          variant="outlined"
          value={expenseEmployeeFilter}
          onChange={(e) => setExpenseEmployeeFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Date From"
          type="date"
          value={expenseDateFrom}
          onChange={(e) => setExpenseDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Date To"
          type="date"
          value={expenseDateTo}
          onChange={(e) => setExpenseDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <Button
          variant="contained"
          startIcon={<AccountBalance />}
          onClick={handleExportExpensesPDF}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Export PDF
        </Button>
      </Box>

      {/* Expenses Table */}
      {expenseLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LoadingAnimation />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenseClaims.map((expense) => (
                  <TableRow key={expense.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person />
                        {expense.user_name}
                      </Box>
                    </TableCell>
                    <TableCell>{new Date(expense.expense_date).toLocaleDateString()}</TableCell>
                    <TableCell>{expense.category}</TableCell>
                    <TableCell>
                      {expense.currency} {expense.amount.toFixed(2)}
                      {expense.approved_amount && expense.approved_amount !== expense.amount && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (Approved: {expense.currency} {expense.approved_amount.toFixed(2)})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography noWrap title={expense.description}>
                        {expense.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expense.status}
                        color={
                          expense.status === 'approved' ? 'success' :
                            expense.status === 'rejected' ? 'error' :
                              expense.status === 'paid' ? 'info' :
                                'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenUpdateExpenseDialog(expense)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1)
                            }
                          }}
                        >
                          Update
                        </Button>
                        {hasCapability(user, CAP.HR_ADMIN_ACTIONS) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedExpense(expense);
                              setReviewExpenseDialogOpen(true);
                            }}
                          >
                            Modify
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Expenses Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={expenseTotalPages}
              page={expensePage}
              onChange={(event, value) => setExpensePage(value)}
              color="primary"
            />
          </Box>
        </>
      )}

      {/* Submit Expense Dialog */}
      <Dialog
        open={submitExpenseDialogOpen}
        onClose={() => { setSubmitExpenseDialogOpen(false); fieldErrors.clear(); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            px: 3,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <AccountBalance sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Submit Expense Claim
          </Typography>
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Expense Date"
              type="date"
              value={expenseFormData.expense_date}
              error={fieldErrors.invalid('expense_date')}
              onChange={(e) => {
                fieldErrors.clearField('expense_date');
                setExpenseFormData({ ...expenseFormData, expense_date: e.target.value });
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControl fullWidth required error={fieldErrors.invalid('category')}>
              <InputLabel>Category</InputLabel>
              <Select
                value={expenseFormData.category}
                onChange={(e) => {
                  fieldErrors.clearField('category');
                  setExpenseFormData({ ...expenseFormData, category: e.target.value });
                }}
                label="Category"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                {expenseCategories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Amount"
                type="number"
                value={expenseFormData.amount}
                error={fieldErrors.invalid('amount')}
                onChange={(e) => {
                  fieldErrors.clearField('amount');
                  setExpenseFormData({ ...expenseFormData, amount: e.target.value });
                }}
                fullWidth
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={expenseFormData.currency}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, currency: e.target.value })}
                  label="Currency"
                  MenuProps={getZoomCompensatedSelectMenuProps()}
                >
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              multiline
              rows={3}
              value={expenseFormData.description}
              error={fieldErrors.invalid('description')}
              onChange={(e) => {
                fieldErrors.clearField('description');
                setExpenseFormData({ ...expenseFormData, description: e.target.value });
              }}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => { setSubmitExpenseDialogOpen(false); fieldErrors.clear(); }}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitExpense}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Submit Claim
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Expense Dialog */}
      <Dialog
        open={reviewExpenseDialogOpen}
        onClose={() => setReviewExpenseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            px: 3,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <AccountBalance sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Review Expense Claim
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedExpense && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Claim Details</Typography>
                <Typography><strong>Employee:</strong> {selectedExpense.user_name}</Typography>
                <Typography><strong>Amount:</strong> {selectedExpense.currency} {selectedExpense.amount.toFixed(2)}</Typography>
                <Typography><strong>Category:</strong> {selectedExpense.category}</Typography>
                <Typography><strong>Description:</strong> {selectedExpense.description}</Typography>
              </Box>

              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>Action</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={reviewAction === 'approve' ? 'contained' : 'outlined'}
                    onClick={() => setReviewAction('approve')}
                    color="success"
                  >
                    Approve
                  </Button>
                  <Button
                    variant={reviewAction === 'reject' ? 'contained' : 'outlined'}
                    onClick={() => setReviewAction('reject')}
                    color="error"
                  >
                    Reject
                  </Button>
                  {selectedExpense.status === 'approved' && (
                    <Button
                      variant={reviewAction === 'pay' ? 'contained' : 'outlined'}
                      onClick={() => setReviewAction('pay')}
                      color="info"
                    >
                      Mark as Paid
                    </Button>
                  )}
                </Box>
              </FormControl>

              {reviewAction === 'approve' && (
                <TextField
                  label="Approved Amount (leave empty for full amount)"
                  type="number"
                  value={reviewData.approved_amount}
                  onChange={(e) => setReviewData({ ...reviewData, approved_amount: e.target.value })}
                  fullWidth
                  inputProps={{ step: 0.01, min: 0 }}
                  helperText={`Original amount: ${selectedExpense.currency} ${selectedExpense.amount.toFixed(2)}`}
                />
              )}

              {reviewAction === 'reject' && (
                <TextField
                  label="Rejection Reason"
                  multiline
                  rows={3}
                  value={reviewData.rejection_reason}
                  onChange={(e) => setReviewData({ ...reviewData, rejection_reason: e.target.value })}
                  fullWidth
                  required
                />
              )}

              <TextField
                label="Notes (Optional)"
                multiline
                rows={2}
                value={reviewData.notes}
                onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => setReviewExpenseDialogOpen(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReviewExpense}
            variant="contained"
            disabled={reviewAction === 'reject' && !reviewData.rejection_reason}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {reviewAction === 'approve' && 'Approve Claim'}
            {reviewAction === 'reject' && 'Reject Claim'}
            {reviewAction === 'pay' && 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Expense Dialog */}
      <Dialog
        open={updateExpenseDialogOpen}
        onClose={() => { setUpdateExpenseDialogOpen(false); fieldErrors.clear(); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            px: 3,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <AccountBalance sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Update Expense Claim
          </Typography>
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Expense Date"
              type="date"
              value={updateExpenseFormData.expense_date}
              error={fieldErrors.invalid('expense_date')}
              onChange={(e) => {
                fieldErrors.clearField('expense_date');
                setUpdateExpenseFormData({ ...updateExpenseFormData, expense_date: e.target.value });
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControl fullWidth required error={fieldErrors.invalid('category')}>
              <InputLabel>Category</InputLabel>
              <Select
                value={updateExpenseFormData.category}
                onChange={(e) => {
                  fieldErrors.clearField('category');
                  setUpdateExpenseFormData({ ...updateExpenseFormData, category: e.target.value });
                }}
                label="Category"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                {expenseCategories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Amount"
                type="number"
                value={updateExpenseFormData.amount}
                error={fieldErrors.invalid('amount')}
                onChange={(e) => {
                  fieldErrors.clearField('amount');
                  setUpdateExpenseFormData({ ...updateExpenseFormData, amount: e.target.value });
                }}
                fullWidth
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={updateExpenseFormData.currency}
                  onChange={(e) => setUpdateExpenseFormData({ ...updateExpenseFormData, currency: e.target.value })}
                  label="Currency"
                  MenuProps={getZoomCompensatedSelectMenuProps()}
                >
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              multiline
              rows={3}
              value={updateExpenseFormData.description}
              error={fieldErrors.invalid('description')}
              onChange={(e) => {
                fieldErrors.clearField('description');
                setUpdateExpenseFormData({ ...updateExpenseFormData, description: e.target.value });
              }}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={updateExpenseFormData.status}
                onChange={(e) => setUpdateExpenseFormData({ ...updateExpenseFormData, status: e.target.value })}
                label="Status"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setUpdateExpenseDialogOpen(false);
              setSelectedExpenseForUpdate(null);
              setUpdateExpenseFormData({
                expense_date: '',
                category: '',
                amount: '',
                currency: 'EUR',
                description: '',
                status: ''
              });
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateExpense}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpensesPage;